
import React, { useEffect } from 'react';
import { ArrowRight, CheckCircle2, ShieldCheck, Clock, Zap, UserCheck, Quote, AlertTriangle, Calendar, Heart, Brain, Search, Check } from 'lucide-react';
import logo2 from '../assets/logo2.png';
import ricardo from '../assets/ricardo.png';
import ana from '../assets/ana.png';
import lucas from '../assets/lucas.png';

const LandingPageV2 = ({ onGetStarted }) => {
    useEffect(() => {
        document.documentElement.classList.remove('dark');
        return () => {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme === 'dark') {
                document.documentElement.classList.add('dark');
            }
        };
    }, []);

    const scrollToPricing = () => {
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleSubscribe = async (priceId) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiBaseUrl}/api/create-public-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    price_id: priceId,
                    cancel_url: window.location.href
                })
            });

            const { url } = await response.json();
            if (url) window.location.href = url;
            else alert('Erro ao iniciar checkout. Tente novamente.');
        } catch (err) {
            console.error("Payment error", err);
            alert("Erro ao conectar com o servidor.");
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/20">

            {/* Hero Section */}
            <section className="relative pt-24 pb-36 px-4 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-background via-muted/20 to-primary/5 -z-10" />
                <div className="max-w-4xl mx-auto text-center space-y-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium animate-fade-in-up">
                        <Brain className="w-4 h-4" />
                        <span>Desconstrua sua identidade</span>
                    </div>

                    <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight">
                        Você não é procrastinador.<br className="hidden md:block" />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500">Você só nunca aprendeu a começar.</span>
                    </h1>

                    <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                        "Eu sou procrastinador." "Eu deixo tudo pra última hora."
                        <br />
                        <strong>Mas e se eu te contar que você tá vivendo uma mentira?</strong>
                    </p>

                    <div className="pt-8">
                        <button
                            onClick={scrollToPricing}
                            className="group relative inline-flex items-center justify-center px-10 py-5 text-xl font-bold text-primary-foreground bg-primary rounded-full hover:bg-primary/90 transition-all duration-300 shadow-xl hover:shadow-primary/25 hover:-translate-y-1"
                        >
                            EU ESCOLHO FAZER — COMEÇAR AGORA
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <p className="mt-4 text-sm text-center text-muted-foreground">Experimente por 30 dias sem risco</p>
                    </div>
                </div>
            </section>

            {/* The False Identity */}
            <section className="py-20 bg-muted/30 border-y border-border/50">
                <div className="container mx-auto px-4 max-w-4xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4">A verdade sobre você</h2>
                        <p className="text-xl text-muted-foreground">Você não procrastina tudo. Aposto que:</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {[
                            "Você responde mensagens importantes na hora",
                            "Você consegue maratonar uma série sem nem piscar",
                            "Você não esquece de comer quando tá com fome",
                            "Você lembra do aniversário de quem você ama"
                        ].map((item, i) => (
                            <div key={i} className="flex items-center gap-4 bg-background p-6 rounded-2xl border border-border shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                                </div>
                                <span className="text-lg font-medium">{item}</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 text-center bg-background rounded-3xl p-8 border border-primary/20 shadow-lg max-w-2xl mx-auto">
                        <p className="text-xl font-medium text-foreground">
                            Então o problema não é você. <span className="text-primary font-bold">É o método que você tá usando.</span><br />
                            <span className="text-base text-muted-foreground mt-2 block">Você não precisa de mais força de vontade. Você precisa de um sistema que funcione com o cérebro que você tem.</span>
                        </p>
                    </div>
                </div>
            </section>

            {/* Sunday Scenario (Pain) */}
            <section className="py-24">
                <div className="container mx-auto px-4 max-w-3xl">
                    <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Deixa eu adivinhar seu domingo...</h2>

                    <div className="relative border-l-2 border-primary/20 pl-8 ml-4 md:ml-12 space-y-12">
                        {[
                            { time: "16h00", text: '"Amanhã eu começo a academia de verdade."', icon: Calendar },
                            { time: "16h15", text: 'Abre o Instagram "só pra dar uma olhada rápida."', icon: Search },
                            { time: "18h30", text: 'Ainda no celular. Aquela sensação ruim no peito.', icon: AlertTriangle },
                            { time: "20h00", text: '"Eu deveria estar fazendo alguma coisa produtiva."', icon: Clock },
                            { time: "22h00", text: '"Amanhã eu compenso. Amanhã vai ser diferente."', icon: UserCheck },
                            { time: "Segunda, 16h", text: 'Déjà vu.', icon: Zap, highlight: true }
                        ].map((item, i) => (
                            <div key={i} className="relative group">
                                <div className="absolute -left-[41px] bg-background border border-border p-1.5 rounded-full z-10">
                                    <item.icon className={`w-5 h-5 ${item.highlight ? 'text-destructive' : 'text-muted-foreground'}`} />
                                </div>
                                <div className={`p-6 rounded-2xl border ${item.highlight ? 'bg-destructive/5 border-destructive/20' : 'bg-muted/30 border-border'} transition-all hover:scale-[1.02]`}>
                                    <span className={`block text-sm font-bold mb-1 ${item.highlight ? 'text-destructive' : 'text-primary'}`}>{item.time}</span>
                                    <p className={`text-lg font-medium ${item.highlight ? 'text-destructive' : 'text-foreground'}`}>{item.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <p className="text-lg text-muted-foreground">Você não tá sozinho. <strong>92% dos brasileiros se consideram procrastinadores.</strong> <br />A diferença é que alguns descobriram o truque.</p>
                    </div>
                </div>
            </section>

            {/* The Trick (Strategy) */}
            <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold mb-8">O truque que ninguém te contou</h2>
                    <p className="text-2xl font-medium mb-12 opacity-90">
                        Você não precisa de motivação. <br />
                        <span className="bg-white text-primary px-2 font-black inline-block transform -rotate-1 mt-2">Você precisa de um primeiro passo ridiculamente fácil.</span>
                    </p>

                    <div className="grid md:grid-cols-2 gap-8 text-left max-w-3xl mx-auto">
                        <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20">
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> O Jeito Errado</h3>
                            <p className="mb-4 font-semibold text-lg">"Vou estudar inglês"</p>
                            <ul className="space-y-2 opacity-80 text-sm">
                                <li>• Abrir o computador</li>
                                <li>• Escolher material</li>
                                <li>• Sentar por 1 hora</li>
                                <li>• Tentar não distrair</li>
                            </ul>
                            <p className="mt-6 text-sm font-bold bg-red-500/20 py-2 px-3 rounded-lg border border-red-500/30 text-red-100">Resultado: Cérebro exausto antes de começar.</p>
                        </div>

                        <div className="bg-white text-primary p-8 rounded-3xl shadow-xl transform md:scale-105 border-4 border-white/50">
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> O Jeito SkipLater</h3>
                            <p className="mb-4 font-semibold text-lg">"Abrir o app por 30s"</p>
                            <ul className="space-y-2 text-primary/80 text-sm">
                                <li>• Pegar o celular</li>
                                <li>• Clicar num botão</li>
                            </ul>
                            <p className="mt-10 text-sm font-bold bg-primary/10 py-2 px-3 rounded-lg border border-primary/20">Resultado: Tão fácil que é impossível recusar.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Solution Presentation */}
            <section className="py-24">
                <div className="container mx-auto px-4 max-w-5xl">
                    <div className="text-center mb-16">
                        <span className="text-primary font-semibold tracking-wider text-sm uppercase">O Sistema</span>
                        <h2 className="text-4xl font-bold mt-2">Transformando Procrastinador em Iniciador</h2>
                    </div>

                    <div className="grid gap-8">
                        {[
                            { title: "1. Você escolhe o que tá te perseguindo", desc: '"Começar aquele curso", "Organizar finanças", "Cuidar da saúde"...', icon: Search },
                            { title: "2. Nós destruímos a tarefa", desc: 'Não é "fazer o curso inteiro". É "assistir 3 minutos". Não é "entrar em forma". É "calçar o tênis".', icon: Zap },
                            { title: "3. Você faz o primeiro passo AGORA", desc: 'Leva menos de 5 minutos. Não dá tempo do seu cérebro inventar desculpa.', icon: Clock },
                            { title: "4. A mágica acontece", desc: '90% das vezes, você continua. Porque começar é a parte difícil. Continuar é fácil.', icon: Heart },
                            { title: "5. Nova Identidade", desc: 'Depois de 30 dias, você para de se chamar de procrastinador. Você vira alguém que faz.', icon: UserCheck }
                        ].map((step, i) => (
                            <div key={i} className="flex flex-col md:flex-row gap-6 items-start md:items-center bg-muted/20 p-6 rounded-2xl border border-border hover:border-primary/30 transition-colors">
                                <div className="bg-background border border-border w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 shadow-sm text-primary">
                                    <step.icon className="w-8 h-8" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold">{step.title}</h3>
                                    <p className="text-muted-foreground mt-1 text-lg">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Transformation Timeline */}
            <section className="py-20 bg-muted/30">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold text-center mb-12">O que muda (de verdade)</h2>
                    <div className="grid md:grid-cols-4 gap-4">
                        {[
                            { title: "Semana 1", text: "Alívio viciante de começar." },
                            { title: "Semana 2", text: "As pessoas notam 'você tá diferente'." },
                            { title: "Semana 3", text: "Você se reconhece no espelho." },
                            { title: "Semana 4", text: "Você tem um sistema mais forte que o medo." }
                        ].map((t, i) => (
                            <div key={i} className="bg-background p-6 rounded-xl border border-border shadow-sm text-center md:text-left">
                                <span className="text-primary text-sm font-bold uppercase tracking-wider block mb-2">{t.title}</span>
                                <p className="font-medium text-foreground">{t.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 bg-background">
                <div className="container mx-auto px-4 max-w-6xl">
                    <h2 className="text-3xl font-bold text-center mb-16">Quem já fez a mudança</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { q: "Eu tinha 4 cursos online parados. Em 15 dias, terminei 2 deles. Agora não me chamo mais de procrastinador.", a: "Carlos, 29, Designer", img: ricardo },
                            { q: "Parei de esperar a segunda perfeita. Comecei a fazer na hora, mesmo de qualquer jeito. E foi suficiente.", a: "Fernanda, 36, Advogada", img: ana },
                            { q: "Não era bloqueio criativo. Era falta de sistema. Hoje eu crio todo dia.", a: "Lucas, 24, Músico", img: lucas }
                        ].map((t, i) => (
                            <div key={i} className="bg-muted/30 p-8 rounded-3xl relative flex flex-col">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-primary/20 shrink-0">
                                        <img src={t.img} alt={t.a.split(',')[0]} className="w-full h-full object-cover" />
                                    </div>
                                    <p className="font-bold text-foreground text-sm">{t.a}</p>
                                </div>
                                <Quote className="w-8 h-8 text-primary/20 mb-4 absolute top-8 right-8" />
                                <p className="text-lg italic text-muted-foreground">"{t.q}"</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing & CTA */}
            <section id="pricing" className="py-24 px-4 bg-primary/5">
                <div className="container px-4 mx-auto max-w-5xl">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">Quanto custa se transformar?</h2>
                        <p className="text-xl text-muted-foreground">O SkipLater custa <strong>menos que um cafézinho por semana</strong>.<br /> E pode mudar quem você é nos próximos 30 dias.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-center">
                        {/* Monthly Plan */}
                        <div className="bg-background rounded-3xl p-8 border-2 border-border flex flex-col items-center text-center">
                            <h3 className="text-xl font-bold mb-2">Plano Mensal</h3>
                            <div className="mb-6">
                                <span className="text-4xl font-bold">R$ 9,90</span>
                                <span className="text-muted-foreground text-sm font-medium">/mês</span>
                            </div>
                            <button
                                onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_MONTHLY)}
                                className="w-full py-4 rounded-xl border border-input font-bold hover:bg-accent hover:text-accent-foreground transition-all"
                            >
                                Assinar Mensal
                            </button>
                            <p className="text-xs text-muted-foreground mt-4">Sem fidelidade. Cancele quando quiser.</p>
                        </div>

                        {/* Yearly Plan */}
                        <div className="bg-background rounded-3xl p-10 border-2 border-primary shadow-2xl flex flex-col items-center text-center transform md:scale-105 relative">
                            <div className="absolute -top-4 bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">Escolha Inteligente</div>
                            <h3 className="text-2xl font-bold mb-2">Plano Anual</h3>
                            <div className="mb-6">
                                <span className="text-5xl font-bold">R$ 97</span>
                                <span className="text-muted-foreground text-sm font-medium">/ano</span>
                            </div>
                            <p className="text-sm text-green-600 font-bold mb-6">Economize 20%</p>
                            <button
                                onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_YEARLY)}
                                className="w-full py-5 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/25 hover:-translate-y-1"
                            >
                                EU ESCOLHO FAZER AGORA
                            </button>
                            <p className="text-xs text-muted-foreground mt-4">Garantia de 30 dias protegida.</p>
                        </div>
                    </div>

                    <div className="mt-20 text-center max-w-2xl mx-auto">
                        <h3 className="text-2xl font-bold mb-4">A escolha mais importante de hoje</h3>
                        <p className="text-muted-foreground mb-8">
                            Você pode continuar sendo "aquela pessoa que deixa pra depois".<br />
                            Ou pode, em 10 segundos, se tornar <strong>"aquela pessoa que faz"</strong>.
                        </p>
                        <div className="text-sm bg-background border border-border p-4 rounded-lg inline-block">
                            <strong>Garantia de 30 dias:</strong> Se você não sentir a mudança, devolvemos seu dinheiro. Sem perguntas.
                        </div>
                    </div>

                    <div className="mt-12 text-center text-sm text-muted-foreground italic">
                        <p>P.S.: Esse texto foi escrito por um ex-procrastinador. A mudança é real. Só precisa clicar.</p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 bg-background border-t border-border/50">
                <div className="container px-4 mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2">
                        <img src={logo2} alt="SkipLater" className="h-8 w-auto rounded-lg grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all" />
                        <span className="text-sm text-muted-foreground font-medium">SkipLater © 2025</span>
                    </div>

                    <div className="flex gap-6 text-sm text-muted-foreground">
                        <a href="/privacy" className="hover:text-foreground transition-colors">Politica de Privacidade</a>
                        <a href="/terms" className="hover:text-foreground transition-colors">Termos de Uso</a>
                    </div>
                </div>
            </footer>

        </div>
    );
};

export default LandingPageV2;
